import React, { useState, lazy, Suspense, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { MatchSearchLogic, type MatchSearchDependencies } from './logic'
import { TransactionResult } from '../transactions/ui/transaction-row'
import { InvoiceResult } from '../ui-components/invoice-result'
import { useAppContext } from '~/app-context'
import { SearchComponent } from '../ui-components/search'
import { DatePicker } from '../ui-components/date-picker'
import styled from 'styled-components'
import { Button } from '~common/components/button'
import { PossibleColumns } from '../ui-components/invoice-result/logic'
import { DateRange } from '../email-importer/services/utils'

const LazyInvoicesOverview = lazy(() =>
    import('../invoices-overview').then((module) => ({
        default: module.InvoicesOverview,
    })),
)
const LazyTransactionsList = lazy(() =>
    import('../transactions').then((module) => ({
        default: module.TransactionsList,
    })),
)
const LazyGmailSearch = lazy(() =>
    import('../google-api/ui/gmail-search').then((module) => ({
        default: module.GmailSearch,
    })),
)

export const MatchSearch: React.FC<MatchSearchDependencies> = (props) => {
    const { t } = useTranslation('match-search')
    const appContext = useAppContext()
    const { logic, state } = useLogic(MatchSearchLogic, {
        ...props,
        ...appContext,
    })

    // For header, get column widths from cache or default
    const defaultVisibleColumns: PossibleColumns[] = [
        'date',
        'amount',
        'vendorName',
        'invoiceId',
        'reason',
        'referenceText',
        'note',
    ]

    const possibleColumns = [
        { key: 'date' as const, label: 'Date' },
        { key: 'amount' as const, label: 'Amount' },
        { key: 'vendorName' as const, label: 'Vendor Name' },
        { key: 'referenceText' as const, label: 'Reference Text' },
        { key: 'note' as const, label: 'Note' },
        { key: 'createdAt' as const, label: 'Created At' },
    ] as const

    const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns)

    // Derive suggestedIds reactively from cache
    const cache = appContext.services.cache
    const suggestedIds =
        state.loadState !== 'success'
            ? []
            : MatchSearchLogic.getSuggestedIds(
                  props.rootItem,
                  props.itemType,
                  cache,
              )

    React.useEffect(() => {
        logic.initialize()
    }, [logic])

    // Use ref to keep onRowClick stable across renders
    // Must be called before any early returns to follow Rules of Hooks
    // logic is stable (from useRef in useLogic), so we can safely reference it
    const onRowClickRef = useRef<((id: string) => void) | null>(null)
    if (!onRowClickRef.current) {
        onRowClickRef.current = (id: string) => {
            logic.confirmMatch(id)
        }
    }
    // Safe to assert non-null since we initialize it above
    const onRowClick = onRowClickRef.current!

    if (state.loadState !== 'success') {
        return <div>Loading matches...</div>
    }

    const isTransaction = props.itemType === 'transaction'

    const defaultTransactionColumnWidths = {
        date: 80,
        amount: 100,
        vendorName: 200,
        invoiceId: 100,
        referenceText: 100,
        note: 200,
        createdAt: 140,
    }

    const defaultInvoiceColumnWidths = {
        date: 120,
        amount: 150,
        vendorName: 200,
        invoiceId: 150,
        reason: 100,
        createdAt: 140,
        match: 100,
    } as const

    const columnWidths = isTransaction
        ? defaultTransactionColumnWidths
        : defaultInvoiceColumnWidths

    return (
        <MainContainer>
            <Title>{props.title}</Title>
            {/* Header */}
            <HeaderContainer>
                <TableWrapper>
                    <colgroup>
                        {visibleColumns.map((column) => {
                            const width =
                                columnWidths[
                                    column as keyof typeof columnWidths
                                ] ?? 100
                            return (
                                <col
                                    key={column}
                                    style={{ width: `${width}px` }}
                                />
                            )
                        })}
                    </colgroup>
                    <thead>
                        <tr>
                            {visibleColumns.map((column) => (
                                <FakeHeaderCell key={column} />
                            ))}
                        </tr>
                    </thead>
                    <FakeTableBody>
                        {isTransaction ? (
                            <TransactionResult
                                transactionDbId={props.rootItem.id as string}
                                visibleColumns={visibleColumns}
                                dontShowMatches={true}
                            />
                        ) : (
                            <InvoiceResult
                                invoiceDbId={props.rootItem.id as string}
                                visibleColumns={visibleColumns}
                                dontShowMatches={true}
                            />
                        )}
                    </FakeTableBody>
                </TableWrapper>
            </HeaderContainer>
            {/* Search Bar */}
            <SearchBarContainer>
                <SearchComponent
                    placeholder={t('placeholders.search')}
                    onUpdate={(term) => logic.updateSearchTerm(term)}
                    onConfirm={(term) => logic.updateSearchTerm(term, true)}
                    size="medium"
                    autoFocus={true}
                    width="30%"
                    icon="search"
                />
                <DatePicker
                    onChange={(range) =>
                        logic.updateDateRange(range as DateRange)
                    }
                    placeholder={t('placeholders.filterByDate')}
                    onConfirm={(range) =>
                        logic.updateDateRange(range as DateRange, true)
                    }
                    size="medium"
                    width="30%"
                    icon="calendar"
                />
                {isTransaction && (
                    <TabsContainer>
                        <Button
                            variant={
                                state.currentTab === 'invoices'
                                    ? 'primary'
                                    : 'ghost'
                            }
                            size="medium"
                            onClick={() => logic.setCurrentTab('invoices')}
                            label="Invoices"
                        />

                        <Button
                            variant={
                                state.currentTab === 'emails'
                                    ? 'primary'
                                    : 'ghost'
                            }
                            size="medium"
                            onClick={() => logic.setCurrentTab('emails')}
                            label="Emails"
                        />
                    </TabsContainer>
                )}
            </SearchBarContainer>
            <ContentContainer>
                <Suspense fallback={<LoadingBlock />}>
                    {isTransaction ? (
                        state.currentTab === 'invoices' ? (
                            <LazyInvoicesOverview
                                key={`invoices-${props.rootItem.id}`}
                                suggestedIds={suggestedIds}
                                onRowClick={onRowClick}
                                context="match-search"
                                contextTransactionId={
                                    props.rootItem.id as string
                                }
                            />
                        ) : (
                            <LazyGmailSearch
                                key={`emails-${props.rootItem.id}`}
                                context="match-search"
                            />
                        )
                    ) : (
                        <LazyTransactionsList
                            key={`transactions-${props.rootItem.id}`}
                            suggestedIds={suggestedIds}
                            onRowClick={onRowClick}
                            context="match-search"
                            contextInvoiceId={props.rootItem.id as string}
                        />
                    )}
                </Suspense>
            </ContentContainer>
        </MainContainer>
    )
}

const MainContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    width: 100%;
`

const ContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
`

const HeaderContainer = styled.div`
    flex-shrink: 0;
`

const TableWrapper = styled.table`
    width: 100%;
    border-collapse: collapse;
    background: ${({ theme }) => theme.colors.greyScale[400]};
`

const FakeHeaderCell = styled.th`
    padding: 0;
    border: none;
    height: 0;
    line-height: 0;
    visibility: hidden;
`

const ColumnsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.greyScale[400]};
`

const ColumnSelector = styled.div`
    position: absolute;
    top: 100px; /* Adjust as needed */
    left: 200px; /* Adjust as needed */
    background: white;
    border: 1px solid #ccc;
    padding: 8px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const TabsContainer = styled.div`
    display: flex;
    flex: 1;
`

const SearchBarContainer = styled.div`
    flex-shrink: 0;
    padding: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
    display: flex;
    gap: 16px;
    align-items: center;
    width: 100%;
`

const Title = styled.h1`
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    padding-left: ${({ theme }) => theme.spacing[4]};
`

const LoadingBlock = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: ${({ theme }) => theme.spacing[40]};
    width: 100%;
`

const FakeTableBody = styled.tbody`
    border-bottom: 1px solid ${({ theme }) => theme.colors.greyScale[300]};
`
